import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { SITE, WA_BOOK, scrollToId } from "../../lib/site";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
    const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <section
            id="home"
            ref={ref}
            data-testid="hero-section"
            className="relative flex min-h-[100svh] items-end overflow-hidden"
        >
            <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
                <img
                    src="/images/hero.jpg"
                    alt="Candlelit luxury spa suite at Flammingo Luxurious Spa, a luxury spa in Vashi Navi Mumbai"
                    className="h-full w-full object-cover"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-emerald-950 via-emerald-950/55 to-emerald-950/35" />
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/70 via-transparent to-transparent" />

            <motion.div
                style={{ opacity: fade }}
                className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-40 md:px-12 md:pb-32"
            >
                <motion.img
                    src="/logo.png"
                    alt="Flammingo Luxurious Spa emblem"
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.2, delay: 0.1, ease: EASE }}
                    className="mb-10 h-24 w-24 rounded-full object-cover ring-1 ring-gold/60 md:h-28 md:w-28"
                />
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.35 }}
                    className="mb-6 text-xs font-medium uppercase tracking-[0.4em] text-gold"
                >
                    Flammingo Luxurious Spa
                </motion.p>
                <h1 className="max-w-4xl font-serif text-5xl leading-[1.04] tracking-tight text-cream sm:text-6xl md:text-7xl lg:text-8xl">
                    <MaskedLine delay={0.45}>Relax.</MaskedLine>
                    <MaskedLine delay={0.6}>Rejuvenate.</MaskedLine>
                    <MaskedLine delay={0.75}>
                        <span className="italic text-gold-light">
                            Rediscover Yourself.
                        </span>
                    </MaskedLine>
                </h1>
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
                    className="mt-7 max-w-xl text-base font-light leading-relaxed tracking-wide text-sage md:text-lg"
                >
                    An elevated wellness experience in the heart of Vashi, Navi
                    Mumbai.
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.25, ease: EASE }}
                    className="mt-10 flex flex-wrap items-center gap-5"
                >
                    <a
                        href={WA_BOOK}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-book-button"
                        className="bg-gold px-9 py-4 text-xs font-medium uppercase tracking-[0.25em] text-emerald-950 transition-colors duration-300 hover:bg-gold-light"
                    >
                        Book Your Experience
                    </a>
                    <button
                        type="button"
                        data-testid="hero-explore-button"
                        onClick={() => scrollToId("#services")}
                        className="border border-cream/30 px-9 py-4 text-xs font-medium uppercase tracking-[0.25em] text-cream transition-colors duration-300 hover:border-gold hover:text-gold"
                    >
                        Explore Our Services
                    </button>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.2, delay: 1.5 }}
                className="absolute right-8 top-1/2 z-10 hidden -translate-y-1/2 flex-col items-center gap-5 lg:flex"
                aria-hidden="true"
            >
                <span className="h-20 w-px bg-gold/40" />
                <span className="text-vertical text-[11px] uppercase tracking-[0.45em] text-sage/80">
                    Vashi, Navi Mumbai
                </span>
                <span className="h-20 w-px bg-gold/40" />
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.2, delay: 1.7 }}
                className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-3 md:flex"
                aria-hidden="true"
            >
                <span className="text-[10px] uppercase tracking-[0.4em] text-sage/70">
                    Scroll
                </span>
                <span className="relative h-12 w-px overflow-hidden bg-cream/15">
                    <span className="absolute left-0 top-0 h-4 w-px animate-scroll-cue bg-gold" />
                </span>
            </motion.div>
        </section>
    );
};

export default Hero;
